/**
 * Canonical SuperApp document theme bootstrap.
 * Preference: system | light | dark (default system; resolve fallback light).
 * Must stay in sync with index.html inline anti-flash script.
 */

export type ThemePreference = "system" | "light" | "dark";

export const THEME_PREF_STORAGE_KEY = "tasallot_theme_preference";
/** Legacy mirror — boolean string for older readers; derived from resolved theme. */
export const THEME_LEGACY_DARK_KEY = "tasallot_dark_mode";

export function getSystemPrefersDark(): boolean {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

export function normalizeThemePreference(raw: string | null | undefined): ThemePreference | null {
  if (raw === "system" || raw === "light" || raw === "dark") return raw;
  return null;
}

/** Migrate legacy tasallot_dark_mode → preference; default SYSTEM. */
export function readThemePreference(): ThemePreference {
  try {
    const urlTheme = new URLSearchParams(window.location.search).get("theme");
    if (urlTheme === "light" || urlTheme === "dark" || urlTheme === "system") {
      return urlTheme;
    }
    const stored = normalizeThemePreference(localStorage.getItem(THEME_PREF_STORAGE_KEY));
    if (stored) return stored;

    const legacy = localStorage.getItem(THEME_LEGACY_DARK_KEY);
    if (legacy === "true") return "dark";
    if (legacy === "false") return "light";
    return "system";
  } catch {
    return "system";
  }
}

export function resolveIsDark(preference: ThemePreference): boolean {
  if (preference === "dark") return true;
  if (preference === "light") return false;
  return getSystemPrefersDark();
}

/** @deprecated Prefer readThemePreference + resolveIsDark */
export function readStoredDarkMode(): boolean {
  return resolveIsDark(readThemePreference());
}

export function applyDocumentTheme(isDark: boolean): void {
  const root = document.documentElement;
  root.classList.toggle("dark", isDark);
  root.classList.toggle("light", !isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
  root.dataset.theme = isDark ? "dark" : "light";
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", isDark ? "#020817" : "#F8FAFC");
  } catch {
    /* ignore */
  }
}

export function persistThemePreference(preference: ThemePreference, isDark: boolean): void {
  try {
    localStorage.setItem(THEME_PREF_STORAGE_KEY, preference);
    localStorage.setItem(THEME_LEGACY_DARK_KEY, String(isDark));
  } catch {
    /* ignore */
  }
}

export function bootstrapDocumentTheme(): boolean {
  const preference = readThemePreference();
  const isDark = resolveIsDark(preference);
  applyDocumentTheme(isDark);
  persistThemePreference(preference, isDark);
  return isDark;
}
